/**
 * SharePoint Save Button Component
 * A split button for saving profiles to SharePoint with folder selection
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Folder as FolderIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { SharePointBrowserDialog } from '../../../components/shared';
import { apiService } from '../../../services/api';
import type { SharePointItem, SharePointSite } from '../../../components/shared/types/sharepoint';

interface SharePointSaveButtonProps {
  pdfFile: File | null;
  disabled?: boolean;
  onSaveComplete?: (result: any) => void;
  onError?: (error: string) => void;
  profileUserId?: string;
  preferredFilename?: string;
}

interface FolderPreference {
  site_id: string;
  drive_id: string;
  folder_path: string;
  folder_name: string;
  web_url?: string;
  updated_at: string;
  site_key?: string;
}

const toFolderPreference = (data: any | null | undefined): FolderPreference | null => {
  if (!data) {
    return null;
  }

  const siteId = data.site_id ?? data.siteId;
  const driveId = data.drive_id ?? data.driveId;
  const folderPath = data.folder_path ?? data.folderPath;
  const folderName = data.folder_name ?? data.folderName;

  if (!siteId || !driveId || !folderPath || !folderName) {
    return null;
  }

  return {
    site_id: siteId,
    drive_id: driveId,
    folder_path: folderPath,
    folder_name: folderName,
    web_url: data.web_url ?? data.webUrl,
    updated_at: data.updated_at ?? data.updatedAt ?? new Date().toISOString(),
    site_key: data.site_key ?? data.siteKey
  };
};

const normalizeErrorMessage = (detail: unknown, fallback: string, originalError?: any): string => {
  if (!detail) {
    return originalError?.message || fallback;
  }

  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail)) {
    const parts = detail.map((item) => {
      if (!item) {
        return '';
      }
      if (typeof item === 'string') {
        return item;
      }
      if (typeof item === 'object') {
        const obj = item as Record<string, unknown>;
        return (obj.msg as string) || (obj.message as string) || JSON.stringify(obj);
      }
      return String(item);
    }).filter(Boolean);

    if (parts.length > 0) {
      return parts.join('; ');
    }
    return fallback;
  }

  if (typeof detail === 'object') {
    const obj = detail as Record<string, unknown>;
    return (obj.msg as string) || (obj.message as string) || JSON.stringify(obj);
  }

  return String(detail);
};

const stripGraphPrefixes = (path: string): string => {
  if (!path) {
    return '';
  }

  let normalized = path.replace(/\\/g, '/');
  normalized = normalized.replace(/:\//g, '/');
  normalized = normalized.replace(/:\\/, '/');
  normalized = normalized.replace(/^:/, '').replace(/:$/, '');
  normalized = normalized.replace(/^\/+/, '').replace(/\/+$/, '');

  const prefixPatterns = [
    /^drive\/root:?/i,
    /^drives\/[^/]+\/root:?/i,
    /^sites\/[^/]+\/drives\/[^/]+\/root:?/i,
    /^sites\/[^/]+\/drive\/root:?/i
  ];

  prefixPatterns.forEach(pattern => {
    normalized = normalized.replace(pattern, '');
  });

  return normalized.replace(/^\/+/, '').replace(/\/+$/, '');
};

const buildFolderPath = (item: SharePointItem): string => {
  const parentPath = stripGraphPrefixes(item.path || '');

  if (!parentPath) {
    return item.name;
  }

  return `${parentPath}/${item.name}`.replace(/\/+/g, '/');
};

const SharePointSaveButtonComponent: React.FC<SharePointSaveButtonProps> = ({
  pdfFile,
  disabled = false,
  onSaveComplete,
  onError,
  profileUserId,
  preferredFilename
}) => {
  const [open, setOpen] = useState(false);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [folderPreference, setFolderPreference] = useState<FolderPreference | null>(null);
  const [loadingPreference, setLoadingPreference] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  // Load user's folder preference on mount
  useEffect(() => {
    let cancelled = false;

    const loadFolderPreference = async () => {
      // Prevent multiple concurrent loads
      if (loadingRef.current) {
        return;
      }

      loadingRef.current = true;
      setLoadingPreference(true);

      try {
        const response = await apiService.get('/api/sharepoint/profile/folder-preference', {
          params: profileUserId ? { user_id: profileUserId } : undefined,
          timeout: 5000 // Set a shorter timeout for preference loading
        });

        if (!cancelled) {
          const normalizedPreference = toFolderPreference(response);
          if (normalizedPreference) {
            setFolderPreference(normalizedPreference);
          }
        }
      } catch (error: any) {
        if (!cancelled) {
          // Silently fail for preference loading - it's not critical
          if (error.code !== 'ECONNABORTED') {
            console.debug('Could not load folder preference:', error.message);
          }
        }
      } finally {
        if (!cancelled) {
          setLoadingPreference(false);
          loadingRef.current = false;
        }
      }
    };

    loadFolderPreference();

    return () => {
      cancelled = true;
      loadingRef.current = false;
    };
  }, [profileUserId]);

  useEffect(() => {
    if (disabled || saving || !pdfFile) {
      setOpen(false);
    }
  }, [disabled, saving, pdfFile]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  const handleSaveClick = async () => {
    if (disabled || saving) {
      return;
    }

    if (!pdfFile) {
      setErrorMessage('No PDF file to save');
      return;
    }

    if (!folderPreference) {
      // No folder preference set, open browser
      setBrowserOpen(true);
      return;
    }

    // Save to SharePoint using existing preference
    await saveToSharePoint();
  };

  const handleSelectFolder = () => {
    setOpen(false);
    setBrowserOpen(true);
  };

  const saveToSharePoint = async (newPreference?: FolderPreference) => {
    if (disabled || saving) {
      return;
    }

    if (!pdfFile) {
      setErrorMessage('No PDF file to save');
      return;
    }

    const preference = newPreference || folderPreference;
    if (!preference) {
      setErrorMessage('No folder selected');
      return;
    }

    setSaving(true);
    setErrorMessage(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', pdfFile);
      const filenameToUse = preferredFilename || pdfFile.name || 'profile.pdf';
      formData.append('filename', filenameToUse);
      formData.append('site_id', preference.site_id);
      formData.append('drive_id', preference.drive_id);
      formData.append('folder_path', preference.folder_path);
      if (profileUserId) {
        formData.append('profile_user_id', profileUserId);
      }

      // Upload to SharePoint
      const response = await apiService.getInstance().post('/api/sharepoint/profile/save-pdf', formData);

      setSuccessMessage(`Profile saved to ${preference.folder_name}`);

      if (onSaveComplete) {
        onSaveComplete(response.data);
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail ?? error?.response?.data;
      const message = normalizeErrorMessage(detail, 'Failed to save to SharePoint', error);
      setErrorMessage(message);

      if (onError) {
        onError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleFolderSelected = async (
    item: SharePointItem,
    site: SharePointSite,
    driveId: string
  ) => {
    try {
      // Build the full folder path
      const folderPath = buildFolderPath(item);

      // Save folder preference
      const preferenceData = {
        site_id: site.siteId,
        site_key: site.id,
        drive_id: driveId,
        folder_path: folderPath,
        folder_name: item.name,
        web_url: item.webUrl
      };

      // Close browser immediately to reflect the selection
      setBrowserOpen(false);

      const response = await apiService.post('/api/sharepoint/profile/folder-preference', {
        ...preferenceData,
        user_id: profileUserId
      });

      const savedPreference = toFolderPreference(response?.preference ?? preferenceData);
      if (savedPreference) {
        setFolderPreference(savedPreference);
      } else {
        // Fallback to local value if API did not return preference structure
        setFolderPreference({
          ...preferenceData,
          updated_at: new Date().toISOString()
        });
      }

      setSuccessMessage(`Folder preference updated to ${item.name}`);
    } catch (error: any) {
      const detail = error?.response?.data?.detail ?? error?.response?.data;
      const message = normalizeErrorMessage(detail, 'Failed to save folder preference', error);
      setErrorMessage(message);

      if (onError) {
        onError(message);
      }
    }
  };

  const buttonText = saving
    ? 'Saving...'
    : loadingPreference
    ? 'Loading...'
    : folderPreference
    ? `Save to ${folderPreference.folder_name}`
    : 'Save to SharePoint';

  const resolvedSiteKey = folderPreference?.site_key || 'hr';
  const resolvedDriveId = folderPreference?.drive_id;
  const resolvedInitialPath = folderPreference?.folder_path || '';
  const resolvedDriveName = folderPreference?.drive_id
    ? undefined
    : 'Professional Profile Folders';

  return (
    <>
      <div
        className={`sharepoint-split ${disabled || saving || !pdfFile ? 'disabled' : ''}`}
        ref={anchorRef}
        aria-label="SharePoint save button"
      >
        <button
          type="button"
          className="sharepoint-btn sharepoint-btn-primary"
          onClick={handleSaveClick}
          disabled={disabled || saving || !pdfFile}
        >
          {saving ? (
            <CircularProgress size={16} color="inherit" className="sharepoint-spinner" />
          ) : (
            <CloudUploadIcon fontSize="small" />
          )}
          {buttonText}
        </button>
        <button
          type="button"
          className="sharepoint-btn sharepoint-btn-secondary"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select folder"
          aria-haspopup="menu"
          onClick={handleToggle}
          disabled={disabled || saving || !pdfFile}
        >
          <ArrowDropDownIcon fontSize="small" />
        </button>
      </div>

      <Popper
        sx={{ zIndex: 1 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom'
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  <MenuItem onClick={handleSelectFolder}>
                    <FolderIcon sx={{ mr: 1 }} />
                    {folderPreference ? 'Change Folder' : 'Select Folder'}
                  </MenuItem>
                  {folderPreference && (
                    <MenuItem disabled>
                      <CheckIcon sx={{ mr: 1, color: 'success.main' }} />
                      Current: {folderPreference.folder_name}
                    </MenuItem>
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <SharePointBrowserDialog
        open={browserOpen}
        mode="folder"
        title="Select Your Profile Folder"
        description="Choose the folder where your professional profile will be saved in SharePoint"
        onSelect={handleFolderSelected}
        onCancel={() => setBrowserOpen(false)}
        initialSite={resolvedSiteKey}
        initialDrive={resolvedDriveId}
        initialDriveName={resolvedDriveName}
        initialPath={resolvedInitialPath}
        defaultItemType="folders"
        hideFilters
        hiddenItemPatterns={['_*']}
        showSearch={false}
        maxWidth="md"
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorMessage(null)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export const SharePointSaveButton = React.memo(SharePointSaveButtonComponent);
